import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { APIProvider } from '@vis.gl/react-google-maps';
import { AddressCurrentLocationModalBody } from '../../components/location/address-current-location-modal-body';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { useMapAPI } from '../../apis/map-api';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function useAddressCurrentLocationModal() {
  const storage = useStorage();
  const { config, menuInfo } = storage;
  const analytics = useAnalytics();
  const { analyticsPageView } = analytics;
  const { t } = useTranslation();
  const { saveMapBaseLocationFromRemote } = useMapAPI();
  const { handleErrorOnModal } = useErrorHandler();
  const { swal } = useSwal({ storage });

  const focusedRef = useRef(false);

  const didOpen = async () => {
    analyticsPageView('Address Current Location');
    focusedRef.current = true;
  };

  const didDestroy = async () => {
    focusedRef.current = false;
  };

  const onMessage = (message) => {
    if (_.isEmpty(message)) {
      swal.resetValidationMessage();
    } else {
      swal.showValidationMessage(message);
    }
  };

  const onError = (error) => {
    if (
      // eslint-disable-next-line no-undef
      error.code === GeolocationPositionError.PERMISSION_DENIED
    ) {
      swal.showValidationMessage(
        t('address-current-location-modal.permission-denied')
      );
    } else if (
      // eslint-disable-next-line no-undef
      error.code === GeolocationPositionError.POSITION_UNAVAILABLE
    ) {
      swal.showValidationMessage(
        t('address-current-location-modal.position-unavailable')
      );
    } else {
      handleErrorOnModal(error);
    }
  };

  async function show() {
    let placeDetails = null;
    let baseLocation = null;

    const result = await swal.fire({
      title: null,
      html: (
        <APIProvider
          apiKey={config?.maps?.browser?.api_key}
          language={menuInfo?.i18n?.default_language?.tag}
        >
          <AddressCurrentLocationModalBody
            onMessage={onMessage}
            onSuccess={async (pPlaceDetails) => {
              try {
                if (!focusedRef.current) return;

                const response =
                  await saveMapBaseLocationFromRemote(pPlaceDetails);

                baseLocation = response.data;
                placeDetails = pPlaceDetails;

                swal.clickConfirm();
              } catch (error) {
                handleErrorOnModal(error);
              }
            }}
            onError={onError}
          />
        </APIProvider>
      ),
      showConfirmButton: false,
      showDenyButton: false,
      showCancelButton: true,
      didOpen,
      didDestroy,
      // confirmButtonText: 'Tentar novamente',
      cancelButtonText: t('back'),
    });

    result.placeDetails = placeDetails;
    result.baseLocation = baseLocation;

    if (result.isDismissed) {
      result.wasCurrentLocatiotBackButtonClicked = true;
    }

    return result;
  }

  return {
    showAddressCurrentLocationModal: show,
  };
}

export { useAddressCurrentLocationModal };
